/**
 * Memoized time calculation utility
 * Caches time calculations to avoid O(n²) recalculations on every render
 *
 * Time aggregation strategy:
 * - Rollup time = direct time estimate + sum of all children times
 * - Uses parent-child hierarchy (parentId) instead of connections
 */

import type { Node, Connection } from '../types';

// Cache for time calculations
interface CacheEntry {
  hash: string;
  totalTime: number;
}

class TimeCalculationCache {
  private cache = new Map<string, CacheEntry>();

  /**
   * Generate a hash for a node and its children
   * Hash changes only when time estimates or children change
   */
  private generateHash(
    nodeId: string,
    nodes: Record<string, Node>,
    connections: Connection[]
  ): string {
    const node = nodes[nodeId];
    if (!node) {
      return '';
    }

    // Use children from the node's hierarchy
    const children = node.children || [];

    const parts = [
      nodeId,
      node.timeEstimate,
      children.sort().join(','),
    ];

    // Recursively include children hashes
    for (const childId of children) {
      parts.push(this.generateHash(childId, nodes, connections));
    }

    return parts.join('|');
  }

  /**
   * Calculate total time for a node and all its children
   * Returns cached result if hash hasn't changed
   *
   * Uses parent-child hierarchy instead of connections
   */
  calculateTotalTime(
    nodeId: string,
    nodes: Record<string, Node>,
    connections: Connection[]
  ): number {
    const node = nodes[nodeId];
    if (!node) {
      return 0;
    }

    // Generate hash for this hierarchy tree
    const hash = this.generateHash(nodeId, nodes, connections);

    // Check cache
    const cached = this.cache.get(nodeId);
    if (cached && cached.hash === hash) {
      return cached.totalTime;
    }

    // Calculate recursively
    let total = parseTime(node.timeEstimate);

    // Add time from all children
    const children = node.children || [];
    for (const childId of children) {
      total += this.calculateTotalTime(childId, nodes, connections);
    }

    // Cache result
    this.cache.set(nodeId, { hash, totalTime: total });

    return total;
  }

  /**
   * Invalidate cache for a specific node and all parent nodes up the hierarchy
   */
  invalidate(nodeId: string, nodes: Record<string, Node>, connections: Connection[]) {
    this.cache.delete(nodeId);

    // Also invalidate parent node (since parent's total time includes this node)
    const node = nodes[nodeId];
    if (node && node.parentId && nodes[node.parentId]) {
      this.invalidate(node.parentId, nodes, connections);
    }
  }

  /**
   * Clear entire cache (useful for undo/redo operations)
   */
  clear() {
    this.cache.clear();
  }

  /**
   * Get cache statistics for debugging
   */
  getStats() {
    return {
      size: this.cache.size,
      entries: Array.from(this.cache.entries()).map(([id, entry]) => ({
        id,
        hash: entry.hash.substring(0, 50) + '...',
        totalTime: entry.totalTime,
      })),
    };
  }
}

// Singleton instance
export const timeCache = new TimeCalculationCache();

/**
 * Parse time estimate string to hours
 * Examples: "2h" -> 2, "3d" -> 24, "1w" -> 40, "2m" -> 320
 */
export function parseTime(time: string): number {
  if (!time) return 0;
  const match = time.match(/^(\d+(?:\.\d+)?)\s*([hdwm])?$/i);
  if (!match) return 0;

  const value = parseFloat(match[1]);
  const unit = match[2]?.toLowerCase() || 'h';

  const hoursPerUnit: Record<string, number> = {
    h: 1,
    d: 8,
    w: 40,
    m: 160,
  };

  return value * (hoursPerUnit[unit] || 1);
}

/**
 * Format hours to human-readable time string
 * Examples: 2 -> "2h", 16 -> "2d", 80 -> "2w", 320 -> "2m"
 */
export function formatTime(hours: number): string {
  if (hours === 0) return '';

  // Choose appropriate unit
  if (hours < 8) {
    return `${hours}h`;
  } else if (hours < 40) {
    const days = hours / 8;
    return days % 1 === 0 ? `${days}d` : `${days.toFixed(1)}d`;
  } else if (hours < 160) {
    const weeks = hours / 40;
    return weeks % 1 === 0 ? `${weeks}w` : `${weeks.toFixed(1)}w`;
  } else {
    const months = hours / 160;
    return months % 1 === 0 ? `${months}m` : `${months.toFixed(1)}m`;
  }
}

/**
 * Calculate and format total time for a node (including dependencies)
 * Uses memoized cache for performance
 */
export function calculateTotalTimeFormatted(
  nodeId: string,
  nodes: Record<string, Node>,
  connections: Connection[]
): string {
  const hours = timeCache.calculateTotalTime(nodeId, nodes, connections);
  return formatTime(hours);
}

/**
 * Check if a node has children
 */
export function hasChildren(
  nodeId: string,
  nodes: Record<string, Node>
): boolean {
  const node = nodes[nodeId];
  return node ? (node.children || []).length > 0 : false;
}

/**
 * Check if a node has dependencies (incoming connections)
 * @deprecated Use hasChildren instead for hierarchy-based checking
 */
export function hasDependencies(
  nodeId: string,
  connections: Connection[]
): boolean {
  return connections.some(conn => conn.toId === nodeId);
}
